import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatDateTime } from '../lib/format'
import type { OnCallData, OnCallMember, OnCallPlan } from '../lib/types'
import { ErrorBox, Loading } from '../components/Feedback'
import { StatusBadge } from '../components/Status'
import { Eskalation } from '../components/Eskalation'

const DAUERN: [number, string][] = [
  [1, 'täglich'],
  [7, 'wöchentlich'],
  [14, 'alle zwei Wochen'],
]

/** Nächster Montag als Vorschlag für den Beginn eines Wochenplans. */
function naechsterMontag(): string {
  const d = new Date()
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7))
  return d.toISOString().slice(0, 10)
}

/**
 * Bereitschaftspläne: wer wann dran ist, im Wechsel, mit Vertretungen.
 * Kanäle für E-Mail, SMS und Anruf können statt fester Empfänger an „die
 * Bereitschaft“ senden; die Zentrale fragt beim Senden, wer gerade dran ist.
 */
export function BereitschaftTab() {
  const queryClient = useQueryClient()
  const [bearbeiten, setBearbeiten] = useState<OnCallPlan | 'neu' | null>(null)
  const daten = useQuery({
    queryKey: ['bereitschaft'],
    queryFn: ({ signal }) => apiGet<OnCallData>('/bereitschaft', signal),
    refetchInterval: 60_000,
  })
  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['bereitschaft'] })
  const loeschen = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/bereitschaft/${id}`),
    onSuccess: neuLaden,
  })

  if (daten.isError) return <ErrorBox error={daten.error} what="Die Bereitschaft" />
  if (daten.isPending) return <Loading what="Bereitschaft" />
  const { plaene, kandidaten } = daten.data

  return (
    <>
      {bearbeiten !== null ? (
        <PlanForm
          vorlage={bearbeiten === 'neu' ? null : bearbeiten}
          kandidaten={kandidaten}
          onFertig={() => {
            setBearbeiten(null)
            neuLaden()
          }}
          onAbbrechen={() => setBearbeiten(null)}
        />
      ) : (
        <div>
          <button type="button" className="btn btn-primary" onClick={() => setBearbeiten('neu')}>
            Bereitschaftsplan anlegen
          </button>
        </div>
      )}

      {plaene.length === 0 && bearbeiten === null ? (
        <section className="card">
          <div className="empty">
            <strong>Noch kein Bereitschaftsplan</strong>
            <span>
              Ein Plan reicht die Bereitschaft reihum weiter, etwa jeden Montag um 8 Uhr. Kanäle für E-Mail, SMS und
              Anruf senden dann an die Person, die gerade dran ist.
            </span>
          </div>
        </section>
      ) : null}

      {plaene.map((p) => (
        <section className="card" key={p.id}>
          <div className="card-head">
            <h2 className="card-title">{p.name}</h2>
            <div className="row" style={{ gap: 6 }}>
              <button type="button" className="btn btn-sm" onClick={() => setBearbeiten(p)}>
                Bearbeiten
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={loeschen.isPending}
                onClick={() => loeschen.mutate(p.id)}
              >
                Löschen
              </button>
            </div>
          </div>
          <div className="card-body stack" style={{ gap: 12 }}>
            <div>
              {p.jetzt ? (
                <>
                  <strong>Jetzt: {p.jetzt.name}</strong>{' '}
                  {p.jetzt.vertretung ? <StatusBadge state="info" label="Vertretung" /> : null}{' '}
                  <span className="muted">bis {formatDateTime(p.jetzt.bis)}</span>
                  {p.mitglieder.find((m) => m.user_id === p.jetzt?.user_id)?.hat_telefon === false ? (
                    <div style={{ color: 'var(--warn)', fontSize: 13 }}>
                      Ohne Mobilnummer im Konto erreichen SMS und Anruf diese Person nicht.
                    </div>
                  ) : null}
                </>
              ) : (
                <span className="muted">Gerade niemand eingeteilt (der Plan beginnt am {p.beginn}).</span>
              )}
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              Wechsel {DAUERN.find(([d]) => d === p.tage)?.[1] ?? `alle ${p.tage} Tage`} um {p.uebergabe} ({p.zeitzone}
              ), Reihenfolge: {p.mitglieder.map((m) => m.name).join(' → ') || '–'}
            </div>
            {p.danach.length > 1 ? (
              <table className="data">
                <thead>
                  <tr>
                    <th>Wer</th>
                    <th>Von</th>
                    <th>Bis</th>
                  </tr>
                </thead>
                <tbody>
                  {p.danach.slice(1).map((s) => (
                    <tr key={s.von}>
                      <td>
                        {s.name} {s.vertretung ? <StatusBadge state="info" label="teils vertreten" /> : null}
                      </td>
                      <td className="mono">{formatDateTime(s.von)}</td>
                      <td className="mono">{formatDateTime(s.bis)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            <Vertretungen plan={p} kandidaten={kandidaten} onGeaendert={neuLaden} />
          </div>
        </section>
      ))}

      <Eskalation />
      {loeschen.isError ? <span style={{ color: 'var(--crit)' }}>{(loeschen.error as Error).message}</span> : null}
    </>
  )
}

function PlanForm({
  vorlage,
  kandidaten,
  onFertig,
  onAbbrechen,
}: {
  vorlage: OnCallPlan | null
  kandidaten: OnCallMember[]
  onFertig: () => void
  onAbbrechen: () => void
}) {
  const [name, setName] = useState(vorlage?.name ?? '')
  const [beginn, setBeginn] = useState(vorlage?.beginn ?? naechsterMontag())
  const [uebergabe, setUebergabe] = useState(vorlage?.uebergabe ?? '08:00')
  const [tage, setTage] = useState(vorlage?.tage ?? 7)
  const [mitglieder, setMitglieder] = useState<string[]>(vorlage?.mitglieder.map((m) => m.user_id) ?? [])
  const [neu, setNeu] = useState('')

  const speichern = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), beginn, uebergabe, tage, zeitzone: 'Europe/Vienna', mitglieder }
      return vorlage ? apiSend('PUT', `/bereitschaft/${vorlage.id}`, body) : apiSend('POST', '/bereitschaft', body)
    },
    onSuccess: onFertig,
  })
  const nameVon = (id: string) => kandidaten.find((k) => k.user_id === id)?.name ?? 'gesperrtes Konto'
  const telefonVon = (id: string) => kandidaten.find((k) => k.user_id === id)?.hat_telefon ?? false
  const verschieben = (i: number, um: number) =>
    setMitglieder((liste) => {
      const out = [...liste]
      const [x] = out.splice(i, 1)
      out.splice(i + um, 0, x!)
      return out
    })

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">{vorlage ? `${vorlage.name} bearbeiten` : 'Bereitschaftsplan anlegen'}</h2>
      </div>
      <form
        className="card-body stack"
        style={{ gap: 12 }}
        onSubmit={(e) => {
          e.preventDefault()
          speichern.mutate()
        }}
      >
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="plan-name">Name</label>
            <input id="plan-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="plan-beginn">Erste Übergabe am</label>
            <input
              id="plan-beginn"
              className="input mono"
              type="date"
              value={beginn}
              onChange={(e) => setBeginn(e.target.value)}
            />
          </div>
          <div className="field" style={{ width: 120 }}>
            <label htmlFor="plan-uebergabe">um</label>
            <input
              id="plan-uebergabe"
              className="input mono"
              type="time"
              value={uebergabe}
              onChange={(e) => setUebergabe(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="plan-tage">Wechsel</label>
            <select id="plan-tage" className="input" value={tage} onChange={(e) => setTage(Number(e.target.value))}>
              {DAUERN.map(([d, text]) => (
                <option key={d} value={d}>
                  {text}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="stack" style={{ gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Reihenfolge</span>
          {mitglieder.length === 0 ? <span className="muted">Noch niemand im Plan.</span> : null}
          <ol style={{ margin: 0, paddingLeft: 20 }} aria-label="Reihenfolge">
            {mitglieder.map((id, i) => (
              <li key={id} style={{ marginBottom: 4 }}>
                <span className="row" style={{ gap: 8, display: 'inline-flex' }}>
                  {nameVon(id)}
                  {!telefonVon(id) ? <StatusBadge state="warn" label="keine Mobilnummer" /> : null}
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-label={`${nameVon(id)} nach oben`}
                    disabled={i === 0}
                    onClick={() => verschieben(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-label={`${nameVon(id)} nach unten`}
                    disabled={i === mitglieder.length - 1}
                    onClick={() => verschieben(i, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-label={`${nameVon(id)} entfernen`}
                    onClick={() => setMitglieder((l) => l.filter((x) => x !== id))}
                  >
                    Entfernen
                  </button>
                </span>
              </li>
            ))}
          </ol>
          <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
            <div className="field" style={{ minWidth: 220 }}>
              <label htmlFor="plan-person">Person</label>
              <select id="plan-person" className="input" value={neu} onChange={(e) => setNeu(e.target.value)}>
                <option value="">auswählen</option>
                {kandidaten
                  .filter((k) => !mitglieder.includes(k.user_id))
                  .map((k) => (
                    <option key={k.user_id} value={k.user_id}>
                      {k.name}
                    </option>
                  ))}
              </select>
            </div>
            <button
              type="button"
              className="btn"
              disabled={!neu}
              onClick={() => {
                setMitglieder((l) => [...l, neu])
                setNeu('')
              }}
            >
              In den Plan aufnehmen
            </button>
          </div>
        </div>

        {speichern.isError ? <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span> : null}
        <div className="row" style={{ gap: 8 }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={speichern.isPending || !name.trim() || mitglieder.length === 0}
          >
            Speichern
          </button>
          <button type="button" className="btn" onClick={onAbbrechen}>
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  )
}

function Vertretungen({
  plan,
  kandidaten,
  onGeaendert,
}: {
  plan: OnCallPlan
  kandidaten: OnCallMember[]
  onGeaendert: () => void
}) {
  const [person, setPerson] = useState('')
  const [von, setVon] = useState('')
  const [bis, setBis] = useState('')
  const [notiz, setNotiz] = useState('')
  const anlegen = useMutation({
    mutationFn: () =>
      apiSend('POST', `/bereitschaft/${plan.id}/vertretungen`, {
        user_id: person,
        von: new Date(von).toISOString(),
        bis: new Date(bis).toISOString(),
        notiz: notiz.trim(),
      }),
    onSuccess: () => {
      setPerson('')
      setVon('')
      setBis('')
      setNotiz('')
      onGeaendert()
    },
  })
  const entfernen = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/vertretungen/${id}`),
    onSuccess: onGeaendert,
  })

  return (
    <div className="stack" style={{ gap: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>Vertretungen</span>
      {plan.vertretungen.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
          {plan.vertretungen.map((v) => (
            <li key={v.id}>
              {v.name}: {formatDateTime(v.von)} bis {formatDateTime(v.bis)}
              {v.notiz ? <span className="muted"> ({v.notiz})</span> : null}{' '}
              <button
                type="button"
                className="btn btn-sm"
                aria-label={`Vertretung von ${v.name} entfernen`}
                onClick={() => entfernen.mutate(v.id)}
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <span className="muted" style={{ fontSize: 13 }}>
          Keine. Eine Vertretung übernimmt für ihren Zeitraum, egal wer laut Plan dran wäre.
        </span>
      )}
      <form
        className="row"
        style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}
        onSubmit={(e) => {
          e.preventDefault()
          anlegen.mutate()
        }}
      >
        <div className="field" style={{ minWidth: 180 }}>
          <label htmlFor={`vertretung-person-${plan.id}`}>Vertretung durch</label>
          <select
            id={`vertretung-person-${plan.id}`}
            className="input"
            value={person}
            onChange={(e) => setPerson(e.target.value)}
          >
            <option value="">auswählen</option>
            {kandidaten.map((k) => (
              <option key={k.user_id} value={k.user_id}>
                {k.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`vertretung-von-${plan.id}`}>von</label>
          <input
            id={`vertretung-von-${plan.id}`}
            className="input mono"
            type="datetime-local"
            value={von}
            onChange={(e) => setVon(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`vertretung-bis-${plan.id}`}>bis</label>
          <input
            id={`vertretung-bis-${plan.id}`}
            className="input mono"
            type="datetime-local"
            value={bis}
            onChange={(e) => setBis(e.target.value)}
          />
        </div>
        <div className="field" style={{ minWidth: 160 }}>
          <label htmlFor={`vertretung-notiz-${plan.id}`}>Notiz</label>
          <input
            id={`vertretung-notiz-${plan.id}`}
            className="input"
            maxLength={200}
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
          />
        </div>
        <button type="submit" className="btn" disabled={!person || !von || !bis || anlegen.isPending}>
          Vertretung eintragen
        </button>
      </form>
      {anlegen.isError ? <span style={{ color: 'var(--crit)' }}>{(anlegen.error as Error).message}</span> : null}
    </div>
  )
}
